import { updateAIParticipants } from '@services/ai-participant';
import {
  draftEnrichOrganization,
  enrichOrganization,
  updateOrganization,
} from '@services/organization';
import { draftEnrichOrgProduct, updateOrgProduct } from '@services/orgProduct';
import { addToReports, updateReport, updateReportIndex, updateReportTitle } from '@services/report';
import {
  resendEmail,
  updateEmail,
  updatePassword,
  updateProfile,
  verifyUpdatedEmail,
} from '@services/user';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router';
import {
  updateInterview,
  updateInterviewFinalize,
  updateInterviewStatus,
} from '../services/interview';
import {
  updateOverallAnalysis,
  updateStudy,
  updateStudyFinalize,
  updateTheme,
} from '../services/survey';

export const useUpdateSurvey = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: ({ id, transformedData }) =>
      toast.promise(updateStudy({ id, transformedData }), {
        loading: 'Saving study...',
        success: 'Study saved successfully!',
        error: (error) =>
          error?.status === 403
            ? 'Not authorized to perform this action'
            : 'Error occurred while saving study. Please try again later.',
      }),
    onSuccess: (res) => {
      navigate('/dynamic-survey/configure', { state: { id: res.id } });
      queryClient.invalidateQueries({ queryKey: ['surveys'] });
      queryClient.invalidateQueries({ queryKey: ['single_survey', res.id] });
    },
  });
};
export const useUpdateSurveyStatus = (id) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, transformedData }) =>
      toast.promise(updateStudyFinalize({ id, transformedData }), {
        loading: 'Updating survey status...',
        success: 'Survey status updated successfully!',
        error: (error) => `An error occurred: ${error?.detail || 'Something went wrong'}`,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['surveys'] });
      queryClient.invalidateQueries({ queryKey: ['survey_details', id] });
      queryClient.invalidateQueries({ queryKey: ['recent_study'] });
    },
    // onError: (error) => {
    //   toast.error(`An error occurred while updating survey status. ${error?.detail}`);
    // },
  });
};
export const useFinalizeSurvey = (id) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: ({ id, transformedData }) =>
      toast.promise(updateStudyFinalize({ id, transformedData }), {
        loading: 'Saving study...',
        success: 'Study saved successfully!',
        error: 'Error occurred while saving study. Please try again later.',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['surveys'] });
      queryClient.invalidateQueries({ queryKey: ['survey_details', id] });
      navigate('/dynamic-survey/share', { state: { id: id } });
    },
  });
};

export const useFinalizeInterview = (id) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, transformedData, processId }) =>
      toast.promise(updateInterviewFinalize({ id, transformedData, processId }), {
        loading: 'Saving study...',
        success: 'Study saved successfully!',
        error: 'Error occurred while saving study. Please try again later.',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['me'] });
      queryClient.invalidateQueries({ queryKey: ['interview_details', id] });
      queryClient.invalidateQueries({ queryKey: ['interview', id] });
      queryClient.invalidateQueries({ queryKey: ['workspaces'] });
      queryClient.invalidateQueries({ queryKey: ['qualitative_study'] });

      // navigate('/interview/share', { state: { id: id } });
    },
  });
};

export const useUpdateInterview = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }) =>
      toast.promise(updateInterview({ id, payload }), {
        loading: 'Saving study...',
        success: 'Study saved successfully!',
        error: 'Error occurred while saving study. Please try again later.',
      }),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['interview', res?.id] });
      queryClient.invalidateQueries({ queryKey: ['qualitative_study'] });
    },
  });
};

export const useUpdateInterviewFinalize = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, transformedData }) =>
      toast.promise(updateInterviewStatus({ id, transformedData }), {
        loading: 'Updating interview status...',
        success: 'Interview status updated successfully!',
        error: (error) => `An error occurred: ${error?.detail || 'Something went wrong'}`,
      }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['qualitative_study'] });
      queryClient.invalidateQueries({ queryKey: ['interview_details', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['single_interview', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['recent_study'] });
    },
  });
};

export const useUpdateProfile = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) =>
      toast.promise(updateProfile(data), {
        loading: 'Updating profile...',
        success: 'Profile updated successfully!',
        error: 'Error occurred while updating profile. Please try again later.',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['me'] });
    },
  });
};

export const useUpdatePassword = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) =>
      toast.promise(updatePassword(data), {
        loading: 'Updating password...',
        success: 'Password updated successfully!',
        error: (error) => `${error?.detail}`,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['me'] });
    },
  });
};

export const useUpdateEmail = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) =>
      toast.promise(updateEmail(data), {
        loading: 'Updating email...',
        success: 'Verification link sent successfully!',
        error: (error) => `${error?.detail}`,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['me'] });
    },
  });
};

export const useResendEmail = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) =>
      toast.promise(resendEmail(data), {
        loading: 'Resending email...',
        success: 'Email resent successfully!',
        error: (error) => `${error?.detail}`,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['me'] });
    },
  });
};

export const useVerifyUpdatedEmail = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) =>
      toast.promise(verifyUpdatedEmail(data), {
        loading: 'Verifying email...',
        success: 'Email verified successfully!',
        error: (error) => `${error?.detail}`,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['me'] });
    },
  });
};
export const useUpdatedAIParticipant = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ participantId, formData }) =>
      toast.promise(updateAIParticipants({ participantId, formData }), {
        loading: 'updating...',
        success: 'Participant updated successfully!',
        error: (error) => `${error?.detail}`,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai_participants'] });
    },
  });
};
export const useAddToReport = () => {
  return useMutation({
    mutationFn: ({ studyId, reportId, formData }) =>
      toast.promise(addToReports({ studyId, reportId, formData }), {
        loading: 'Adding block...',
        success: 'Block added to report!',
        error: (error) => `${error?.detail}`,
      }),
    onSuccess: () => {},
  });
};
export const useUpdateReport = () => {
  return useMutation({
    mutationFn: ({ studyId, reportId, sectionId, formData }) =>
      toast.promise(updateReport({ studyId, reportId, sectionId, formData }), {
        loading: 'Updating...',
        success: 'Report updated successfully!',
        error: (error) => `${error?.detail}`,
      }),
    onSuccess: () => {},
  });
};
export const useUpdateReportIndex = () => {
  return useMutation({
    mutationFn: ({ studyId, reportId, formData }) =>
      updateReportIndex({ studyId, reportId, formData }),
    onSuccess: () => {},
  });
};
export const useUpdateReportTitle = () => {
  return useMutation({
    mutationFn: ({ studyId, reportId, formData }) =>
      updateReportTitle({ studyId, reportId, formData }),
    onSuccess: () => {},
  });
};

export const useUpdateTheme = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (formData) => updateTheme(formData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['survey-question-theme'] });
      queryClient.invalidateQueries({ queryKey: ['survey_question_analysis'] });
    },
    onError: (error) => {
      toast.error(error?.detail || 'Failed to update theme. Please try again.');
    },
  });
};

export const useUpdateOverallAnalysis = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (surveyId) => updateOverallAnalysis(surveyId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['overall_analysis'] });
    },
    onError: (error) => {
      toast.error(error?.detail || 'Failed to update overall analysis. Please try again.');
    },
  });
};

export const useUpdateOrganization = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: ({ id, payload }) => updateOrganization({ id, payload }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
      queryClient.invalidateQueries({ queryKey: ['organization'] });
      toast.success('Organization updated successfully!');
      navigate('/organizations');
    },
    onError: (error) => {
      toast.error(error?.detail || 'Failed to update organization. Please try again.');
    },
  });
};

export const useEnrichOrganization = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (formData) => enrichOrganization(formData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organization'] });
    },
    onError: (error) => {
      toast.error(error?.detail || 'Failed to enrich organization. Please try again.');
    },
  });
};

export const useDraftEnrichOrganization = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ url, name, signal }) => draftEnrichOrganization({ url, name, signal }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organization'] });
    },
    onError: (error) => {
      toast.error(error?.detail || 'Failed to enrich organization. Please try again.');
    },
  });
};

export const useDraftEnrichProduct = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ url, name, signal }) => draftEnrichOrgProduct({ url, name, signal }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
    },
    onError: (error) => {
      toast.error(error?.detail || 'Failed to enrich products. Please try again.');
    },
  });
};

export const useUpdateProduct = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: ({ id, payload }) => updateOrgProduct({ id, payload }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['org_products'] });
      queryClient.invalidateQueries({ queryKey: ['org_product'] });
      toast.success('Product updated successfully!');
      navigate('/products');
    },
    onError: (error) => {
      toast.error(error?.detail || 'Failed to update product. Please try again.');
    },
  });
};
